import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { Invitation, Member, Role } from '@floorpoll/shared';
import { Field, FormError, FormNotice } from '../../components/Form.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useWorkspace } from '../../lib/workspace.js';
import { track } from '../../lib/track.js';

const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin — everything, including people',
  editor: 'Editor — build and present polls',
  viewer: 'Viewer — read-only',
};

/** Stories #7–#10 — the workspace's people. Admin only. */
export function Members() {
  const { active, can } = useWorkspace();
  const { user, refreshMe } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('editor');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const workspaceId = active?.workspaceId;
  const isAdmin = can('member:manage');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const memberResult = await apiRequest<{ members: Member[] }>(
        `/workspaces/${workspaceId}/members`,
      );
      setMembers(memberResult.members);

      if (isAdmin) {
        const inviteResult = await apiRequest<{ invitations: Invitation[] }>(
          `/workspaces/${workspaceId}/invitations`,
        );
        setInvitations(inviteResult.invitations);
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load members.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  async function invite(event: FormEvent) {
    event.preventDefault();
    if (!workspaceId) return;
    setError(null);
    setNotice(null);

    try {
      const result = await apiRequest<{ inviteUrl: string; emailSent: boolean }>(
        `/workspaces/${workspaceId}/invitations`,
        { method: 'POST', body: { email: inviteEmail, role: inviteRole } },
      );
      track('memberInvited');
      setInviteEmail('');
      setNotice(
        result.emailSent
          ? `Invitation sent to ${inviteEmail}.`
          : `We couldn't email ${inviteEmail}. Share this invite link with them instead: ${result.inviteUrl}`,
      );
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send that invitation.');
    }
  }

  async function changeRole(userId: string, role: Role) {
    if (!workspaceId) return;
    setError(null);
    setNotice(null);

    try {
      await apiRequest(`/workspaces/${workspaceId}/members/${userId}`, {
        method: 'PATCH',
        body: { role },
      });
      await load();
      // Changing your own role changes what the console should show you.
      if (userId === user?.userId) await refreshMe();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not change that role.');
    }
  }

  async function removeMember(member: Member) {
    if (!workspaceId) return;
    if (!confirm(`Remove ${member.email} from ${active?.name}?`)) return;
    setError(null);
    setNotice(null);

    try {
      await apiRequest(`/workspaces/${workspaceId}/members/${member.userId}`, { method: 'DELETE' });
      await load();
      if (member.userId === user?.userId) await refreshMe();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not remove that person.');
    }
  }

  async function revoke(invitation: Invitation) {
    if (!workspaceId) return;
    try {
      await apiRequest(`/workspaces/${workspaceId}/invitations/${invitation.invitationId}`, {
        method: 'DELETE',
      });
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not revoke that invitation.');
    }
  }

  if (!active) return <p className="muted">No workspace selected.</p>;

  return (
    <section>
      <h1>People</h1>
      <p className="muted">Who can see and build polls in {active.name}.</p>

      <FormError message={error} />
      <FormNotice message={notice} />

      {isAdmin ? (
        <form className="panel" onSubmit={invite}>
          <h2>Invite someone</h2>
          <div className="row">
            <Field label="Email" htmlFor="invite-email">
              <input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@example.com"
                required
              />
            </Field>
            <Field label="Role" htmlFor="invite-role">
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Role)}
              >
                {(Object.keys(ROLE_LABELS) as Role[]).map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <button type="submit">Send invitation</button>
        </form>
      ) : null}

      <div className="panel">
        <h2>Members</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  {isAdmin ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.userId}>
                    <td>
                      {member.name}
                      <span className="muted block-sm">{member.email}</span>
                    </td>
                    <td>
                      {isAdmin ? (
                        <select
                          aria-label={`Role for ${member.email}`}
                          value={member.role}
                          onChange={(e) => void changeRole(member.userId, e.target.value as Role)}
                        >
                          <option value="admin">Admin</option>
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      ) : (
                        <span className="badge">{member.role}</span>
                      )}
                    </td>
                    {isAdmin ? (
                      <td className="right">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() => void removeMember(member)}
                        >
                          Remove
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isAdmin && invitations.length > 0 ? (
        <div className="panel">
          <h2>Pending invitations</h2>
          <div className="table-scroll">
            <table className="data-table">
              <tbody>
                {invitations.map((invitation) => (
                  <tr key={invitation.invitationId}>
                    <td>
                      {invitation.email}
                      <span className="muted block-sm">
                        {invitation.role} · expires{' '}
                        {new Date(invitation.expiresAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="right">
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => void revoke(invitation)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}
